import { 
	PhoneIncoming, 
	PhoneMissed, 
	Voicemail, 
	PhoneOutgoing, 
	MessageSquare, 
	Mail 
} from 'lucide-react'
import { formatSmartTimestamp } from './formatTimestamp'
import type { Activity } from '@/data/mockOrdersData'

/**
 * Activity type mappings for icons and descriptions
 */
const ACTIVITY_MAPPINGS = {
	// Call activities
	'call-inbound': {
		icon: PhoneIncoming,
		description: (contactName: string) => `${contactName} called you`
	},
	'call-inbound-missed': {
		icon: PhoneMissed,
		description: () => 'You missed a call'
	},
	'call-inbound-voicemail': {
		icon: Voicemail,
		description: (contactName: string) => `${contactName} left a voicemail`
	},
	'call-outbound': {
		icon: PhoneOutgoing,
		description: (contactName: string) => `You called ${contactName}`
	},
	'call-outbound-missed': {
		icon: PhoneMissed,
		description: (contactName: string) => `You called ${contactName}`
	},
	
	// Message/SMS activities
	'message-inbound': {
		icon: MessageSquare,
		description: (contactName: string) => `${contactName} sent a message`
	},
	'message-outbound': {
		icon: MessageSquare,
		description: () => 'You sent a message'
	},
	
	// Email activities
	'email-inbound': {
		icon: Mail,
		description: (contactName: string) => `${contactName} emailed you`
	},
	'email-outbound': {
		icon: Mail,
		description: (contactName: string) => `You emailed ${contactName}`
	}
} as const

type ActivityKey = keyof typeof ACTIVITY_MAPPINGS

/**
 * Get the activity key based on type and direction
 */
function getActivityKey(activity: Activity): ActivityKey | null {
	const { type, direction } = activity
	
	// Handle call activities
	if (type === 'call') {
		if (direction === 'inbound') {
			// Check if it's a missed call or has voicemail
			if (activity.summary?.toLowerCase().includes('missed')) {
				return 'call-inbound-missed'
			}
			if (activity.call_recording_url || activity.transcript) {
				return 'call-inbound-voicemail'
			}
			return 'call-inbound'
		} else if (direction === 'outbound') {
			// Check if it's a missed outbound call
			if (activity.summary?.toLowerCase().includes('missed')) {
				return 'call-outbound-missed'
			}
			return 'call-outbound'
		}
	}
	
	// Handle message activities
	if (type === 'message' || type === 'text') {
		return direction === 'inbound' ? 'message-inbound' : 'message-outbound'
	}
	
	// Handle email activities
	if (type === 'email') {
		return direction === 'inbound' ? 'email-inbound' : 'email-outbound'
	}
	
	return null
}

/**
 * Extract first name from full name
 * 
 * @param fullName - Full display name
 * @returns First name only
 */
function getFirstName(fullName: string): string {
	return fullName.split(' ')[0]
}

/**
 * Format activity string with icon and description
 * 
 * @param activity - The activity object
 * @param contactName - The contact's display name
 * @param fallbackContact - Fallback contact info (phone/email) if name is unknown
 * @returns Object with icon component and formatted text
 */
export function formatActivityString(
	activity: Activity, 
	contactName?: string, 
	fallbackContact?: string
) {
	const activityKey = getActivityKey(activity)
	
	if (!activityKey) {
		// Fallback for unknown activity types
		return {
			icon: MessageSquare,
			text: `${formatSmartTimestamp(activity.created_at)} • ${activity.summary || 'Activity'}`
		}
	}
	
	const mapping = ACTIVITY_MAPPINGS[activityKey]
	
	// Use first name only to keep text shorter
	let displayName = 'Someone'
	if (contactName) {
		displayName = getFirstName(contactName)
	} else if (fallbackContact) {
		displayName = fallbackContact
	}
	
	const description = mapping.description(displayName)
	const timeString = formatSmartTimestamp(activity.created_at)
	
	return {
		icon: mapping.icon,
		text: `${timeString} • ${description}`
	}
}

/**
 * Get contact fallback info (phone or email)
 * 
 * @param contact - Contact object
 * @returns Fallback string (phone or email)
 */
export function getContactFallback(contact: { phone_raw?: string | null; email?: string | null }): string | undefined {
	return contact.phone_raw || contact.email || undefined
}
